/* ============================================================================
   FiveStarsCards — theme JavaScript
   Vanilla, no dependencies, no build step.

   WHAT CHANGED FROM THE ORIGINAL script.js AND WHY
   ------------------------------------------------
   1. The whole thing is now init() / teardown() instead of a single top-to-bottom
      IIFE. Shopify's theme editor destroys and re-renders a section's HTML every
      time a merchant changes a setting, which would silently kill every listener
      bound to an element inside it. init() re-binds them on `shopify:section:load`.
   2. teardown() clears the tap-demo interval and disconnects the IntersectionObserver
      so a re-render can't leave two animation loops running at once.
   3. Listeners that can be delegated (data-act buttons, the FAQ accordion, Escape)
      are bound to `document` once, outside init, so they survive re-renders for free.
   4. Cart state lives at module scope, so an editor re-render doesn't empty the basket.
   5. Thumbnail URLs come from window.FSC_ASSETS (set in layout/theme.liquid) because
      Shopify serves assets from a fingerprinted CDN path, not from img/.

   Everything else — timings, easing, copy, validation, maths — is byte-identical
   to the original.
   ============================================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     Catalogue and basket. Module scope so both survive a re-render.
     ------------------------------------------------------------------ */
  var P = {
    Sticker: { name: 'The Sticker', price: 9.99 },
    Card:    { name: 'The Card',    price: 16.99 },
    Stand:   { name: 'The Stand',   price: 19.97 }
  };
  var KEYS = ['Sticker', 'Card', 'Stand'];
  var cart = { Sticker: 0, Card: 0, Stand: 0 };

  /* ------------------------------------------------------------------
     Shopify variant map — THE ONLY THING YOU NEED TO EDIT.

     Each configurator key maps to the numeric Shopify variant ID it sells.
     Find an ID at Products → (product) → Variants (last number in the URL),
     or by opening /products/<handle>.js and reading variants[].id.

     Leave a value as null and Place order refuses to run rather than
     silently 404-ing, and logs which key is unset to the console.

     `const` because nothing should ever reassign the map at runtime; the
     rest of the file predates it and uses `var`.
     ------------------------------------------------------------------ */
  const SHOPIFY_VARIANTS = {
    Sticker: 58538854744448,   // Sticker · White / PVC
    Card:    58547073220992,   // Card  · White / PVC / Tabletop   ← Counter variant unreachable
    Stand:   58546982388096    // Stand · White / Tabletop / PVC   ← Counter variant unreachable
  };

  /* Optional override from Liquid — see layout/theme.liquid. Lets the IDs live
     in theme settings so they can change without editing JavaScript. Read
     lazily, exactly like thumb() reads FSC_ASSETS, so the inline block in
     <head> and this deferred script can load in any order. */
  function variantId(k) {
    var fromLiquid = window.FSC_VARIANTS && window.FSC_VARIANTS[k];
    return fromLiquid || SHOPIFY_VARIANTS[k];
  }

  /* Shopify mounts the storefront under a locale/market prefix on some setups
     (/en-bg/...). Shopify.routes.root carries that prefix; hard-coding
     '/cart/add.js' breaks those stores. Falls back to '/' on a plain setup. */
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* Thumbnails resolved by Liquid — see layout/theme.liquid.
     Read lazily at call time rather than captured at parse time, so the
     inline FSC_ASSETS block and this deferred script can load in any order. */
  function thumb(k) {
    return (window.FSC_ASSETS && window.FSC_ASSETS[k]) || '';
  }

  /* Sale maths. P[].price is what the customer pays; the list price is
     derived from it, so changing OFF re-prices the whole page in one place.
     Dual price display (BGN + EUR) was mandatory in Bulgaria until 8 Aug 2026.
     Set DUAL = true to bring the lev figures back. */
  var DUAL = false;
  var BGN  = 1.95583;   // fixed conversion rate, not a market rate
  var OFF  = 0.5;

  function list(k)      { return P[k].price / (1 - OFF); }
  function lev(n)       { return (n * BGN).toFixed(2) + ' лв.'; }
  function money(n)     { return '€' + n.toFixed(2); }
  function price(n)     { return DUAL ? money(n) + ' · ' + lev(n) : money(n); }
  function count()      { return KEYS.reduce(function (s, k) { return s + cart[k]; }, 0); }
  function total()      { return KEYS.reduce(function (s, k) { return s + cart[k] * P[k].price; }, 0); }
  function subtotal()   { return KEYS.reduce(function (s, k) { return s + cart[k] * list(k); }, 0); }
  function saved()      { return subtotal() - total(); }
  function setTxt(id, v){ var n = document.getElementById(id); if (n) { n.textContent = v; } }

  /* ------------------------------------------------------------------
     Disposables + element refs, refreshed on every init()
     ------------------------------------------------------------------ */
  var loopTimer = null, resetTimer = null, io = null, onScroll = null;
  var lastTotal = 0, tapVal = 0, tapTarget = 128, opener = null;
  var el = {};

  /* ------------------------------------------------------------------
     Cart rendering — one source of truth, two mount points:
       · #picker  — on-page configurator, always lists all three
       · #drLines — the drawer, lists only what has been added
     ------------------------------------------------------------------ */
  function lineHTML(k) {
    var q = cart[k], lineTotal = q * P[k].price;
    return '<div class="line">' +
      '<img class="lv" alt="" width="48" height="48" src="' + thumb(k) + '">' +
      '<div><div class="line-n">' + P[k].name + '</div>' +
           '<div class="line-u">' + price(P[k].price) + ' each' +
             '<span class="was">' + money(list(k)) + '</span></div></div>' +
      '<div class="line-r">' +
        '<div class="qstep">' +
          '<button class="qbtn" data-act="dec" data-k="' + k + '" aria-label="One fewer ' + P[k].name + '">−</button>' +
          '<span class="qn">' + q + '</span>' +
          '<button class="qbtn" data-act="inc" data-k="' + k + '" aria-label="One more ' + P[k].name + '">+</button>' +
        '</div>' +
        '<div class="line-t' + (q === 0 ? ' zero' : '') + '">' + money(lineTotal) + '</div>' +
      '</div></div>';
  }

  function render() {
    if (!el.picker) { return; }

    var n = count(), t = total();

    el.picker.innerHTML = KEYS.map(function (k) { return lineHTML(k); }).join('');

    var inCart = KEYS.filter(function (k) { return cart[k] > 0; });
    el.drLines.innerHTML = inCart.length
      ? inCart.map(function (k) { return lineHTML(k); }).join('')
      : '<div class="dr-empty"><i>★</i>' +
        '<p>Nothing added yet. Pick the format that fits your counter — the chip inside is the same in all three.</p>' +
        '<button class="btn btn-ghost" data-act="shop">Browse the three formats</button></div>';

    el.badge.textContent = n;
    el.badge.classList.toggle('show', n > 0);
    if (n > 0 && !reduce) {
      el.badge.classList.remove('pop');
      void el.badge.offsetWidth;
      el.badge.classList.add('pop');
    }

    var tStr = price(t), sStr = '−' + money(saved());
    setTxt('drSub', money(subtotal()));  setTxt('pageSub', money(subtotal()));
    setTxt('drSave', sStr); setTxt('drSave2', sStr); setTxt('pageSave', sStr);
    ['drTotal', 'drTotal2', 'pageTotal'].forEach(function (id) { setTxt(id, tStr); });

    if (t !== lastTotal && !reduce) {
      ['drTotal', 'pageTotal'].forEach(function (id) {
        var node = document.getElementById(id);
        if (!node) { return; }
        node.classList.remove('flash');
        void node.offsetWidth;
        node.classList.add('flash');
      });
    }
    lastTotal = t;

    var label = n + (n === 1 ? ' item' : ' items');
    setTxt('drCount', n > 0 ? label : '');
    setTxt('drStep2Count', label);
    el.drFoot.style.display = n > 0 ? '' : 'none';
    el.drNext.disabled = n === 0;
    el.reviewBtn.disabled = n === 0;
    el.reviewBtn.textContent = n === 0 ? 'Add an item to continue' : 'Review order · ' + tStr;
    el.cartBtn.setAttribute('aria-label',
      n === 0 ? 'Your order, empty' : 'Your order, ' + label + ', ' + tStr);
  }

  /* ------------------------------------------------------------------
     Hero tap demonstration
     ------------------------------------------------------------------ */
  function bumpCount() {
    if (!el.cnt) { return; }
    if (reduce) { el.cnt.textContent = tapTarget; return; }
    tapTarget += Math.floor(Math.random() * 3) + 1;
    var from = tapVal, diff = tapTarget - from, t0 = performance.now(), dur = 900;
    (function step(now) {
      var p = Math.min((now - t0) / dur, 1);
      tapVal = Math.round(from + diff * (1 - Math.pow(1 - p, 3)));
      el.cnt.textContent = tapVal;
      if (p < 1) { requestAnimationFrame(step); }
    })(performance.now());
  }

  function playTap() {
    if (!el.scene) { return; }
    clearTimeout(resetTimer);
    el.scene.classList.remove('tapping');
    void el.scene.offsetWidth;              // restart the CSS timeline
    el.scene.classList.add('tapping');
    bumpCount();
    resetTimer = setTimeout(function () { el.scene.classList.remove('tapping'); }, 5200);
  }

  function startLoop() {
    clearInterval(loopTimer);
    loopTimer = setInterval(playTap, 7000);
  }

  /* ------------------------------------------------------------------
     Drawer
     ------------------------------------------------------------------ */
  function goStep(n) {
    if (!el.drawer) { return; }
    Array.prototype.forEach.call(el.drawer.querySelectorAll('.dr-step'), function (s) {
      s.classList.toggle('on', s.dataset.step === String(n));
    });
  }

  function openDrawer(step) {
    if (!el.drawer) { return; }
    opener = document.activeElement;
    if (step) { goStep(step); }
    el.drawer.classList.add('on');
    el.scrim.classList.add('on');
    document.body.style.overflow = 'hidden';
    setTimeout(function () {
      var x = document.getElementById('drClose');
      if (x) { x.focus(); }
    }, reduce ? 0 : 380);
  }

  function closeDrawer() {
    if (!el.drawer) { return; }
    el.drawer.classList.remove('on');
    el.scrim.classList.remove('on');
    document.body.style.overflow = '';
    if (opener && opener.focus) { opener.focus(); }
  }

  /* ------------------------------------------------------------------
     Place order — real Shopify checkout.

     Validates exactly as the demo did, pushes the basket into the Shopify
     cart via the Ajax Cart API with the three fields attached as line item
     properties, then hands the customer to Shopify's own checkout.

     async/await is safe here: the theme already requires IntersectionObserver
     and Element.closest, so it has never supported IE11 anyway.
     ------------------------------------------------------------------ */

  /* One POST helper for both cart endpoints. Shopify answers a failure with a
     JSON body carrying `description` (human readable) and `message` (terse),
     so the real reason — sold out, variant not found, quantity capped — is
     surfaced instead of a generic failure. */
  async function postJSON(url, body) {
    var res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',        // the cart lives in a first-party cookie
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    });

    var data = null;
    try { data = await res.json(); } catch (e) { /* 204 or non-JSON body */ }

    if (!res.ok) {
      throw new Error(
        (data && (data.description || data.message)) || ('Cart error ' + res.status)
      );
    }
    return data;
  }

  /* Empty the Shopify cart before adding.

     /cart/add.js ADDS to whatever is already there. Without this, a customer
     who reaches checkout, presses Back, changes a quantity and presses Place
     order again gets both attempts charged. Clearing first makes this drawer
     the single source of truth for what is in the cart. */
  function clearShopifyCart() {
    return postJSON(root() + 'cart/clear.js', {});
  }

  function addToShopifyCart(items) {
    return postJSON(root() + 'cart/add.js', { items: items });
  }

  /* Network failures throw a TypeError whose message ('Failed to fetch') means
     nothing to a customer. Anything Shopify itself said is passed through. */
  function friendlyError(err) {
    var m = (err && err.message) ? String(err.message) : '';
    if (!m || /failed to fetch|networkerror|load failed/i.test(m)) {
      return 'We could not reach the checkout. Check your connection and try again.';
    }
    return m;
  }

  var placing = false;   // guards against a double tap firing two adds

  async function placeOrder() {
    var bizEl  = document.getElementById('biz');
    var mailEl = document.getElementById('mail');
    var linkEl = document.getElementById('link');
    var errEl  = document.getElementById('err');
    var btn    = document.getElementById('drPlace');
    if (!bizEl || !mailEl || !errEl) { return; }
    if (placing) { return; }

    var biz        = bizEl.value.trim();
    var mail       = mailEl.value.trim();
    var reviewLink = linkEl ? linkEl.value.trim() : '';

    /* --- validation: byte-identical to the demo build --- */
    if (!biz) {
      errEl.textContent = 'Add your business name so we know what to print.';
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(mail)) {
      errEl.textContent = 'Check the email address — the payment link goes there.';
      return;
    }

    /* --- guards the demo never needed --- */
    var chosen = KEYS.filter(function (k) { return cart[k] > 0; });
    if (!chosen.length) {
      errEl.textContent = 'Your order is empty — add an item first.';
      return;
    }

    var unset = chosen.filter(function (k) { return !variantId(k); });
    if (unset.length) {
      errEl.textContent = 'Checkout is not configured yet — please get in touch and we will take the order by email.';
      console.error('[FiveStarsCards] No Shopify variant ID for: ' + unset.join(', ') +
                    '. Set them in SHOPIFY_VARIANTS in assets/script.js.');
      return;
    }
    errEl.textContent = '';

    /* Line item properties. A key with an empty string value is discarded by
       Shopify, so the optional review link is only attached when filled in —
       otherwise the order shows a stray blank row. None of the keys start with
       an underscore, which is what keeps them visible on the order. */
    var props = {
      'Business name': biz,
      'Email': mail
    };
    if (reviewLink) {
      /* Normalisation, not validation — the field stays optional and free-form.
         Delete these two lines if you would rather store it exactly as typed. */
      props['Google review link'] =
        /^https?:\/\//i.test(reviewLink) ? reviewLink : 'https://' + reviewLink;
    }

    var items = chosen.map(function (k) {
      return {
        id: variantId(k),
        quantity: cart[k],
        properties: props
      };
    });

    placing = true;
    var label = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Working…'; }

    try {
      await clearShopifyCart();
      await addToShopifyCart(items);
      /* Deliberately no re-enable and no goStep(3) on success — the page is
         navigating away, and restoring the button would open a window for a
         second submission during the redirect. */
      window.location.href = root() + 'checkout';
    } catch (err) {
      errEl.textContent = friendlyError(err);
      if (btn) { btn.disabled = false; btn.textContent = label; }
      placing = false;
    }
  }

  /* ------------------------------------------------------------------
     Document-level listeners — bound once, survive section re-renders
     ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    /* FAQ accordion, one open at a time */
    var q = e.target.closest ? e.target.closest('.qa-q') : null;
    if (q) {
      var qa = q.parentElement, isOpen = qa.classList.contains('open');
      Array.prototype.forEach.call(document.querySelectorAll('.qa'), function (x) {
        x.classList.remove('open');
      });
      if (!isOpen) { qa.classList.add('open'); }
      return;
    }

    var b = e.target.closest ? e.target.closest('[data-act]') : null;
    if (!b) { return; }
    var k = b.dataset.k;

    if (b.dataset.act === 'inc') { cart[k] = Math.min(cart[k] + 1, 99); render(); }

    if (b.dataset.act === 'dec') { cart[k] = Math.max(cart[k] - 1, 0); render(); }

    if (b.dataset.act === 'add') {
      cart[k] += 1;
      render();
      var was = b.textContent;
      b.textContent = 'Added ✓';
      setTimeout(function () { b.textContent = was; }, 1100);
      openDrawer(1);
    }

    if (b.dataset.act === 'shop') {
      closeDrawer();
      var shop = document.getElementById('shop');
      if (shop) { shop.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' }); }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && el.drawer && el.drawer.classList.contains('on')) {
      closeDrawer();
    }
  });

  /* Back button out of Shopify checkout.

     placeOrder() leaves the button disabled on success because the page is
     navigating away. Browsers restore this page from the back/forward cache
     with JavaScript state intact, so without this the customer returns from
     /checkout to a dead 'Working…' button and can never re-order.
     Fires on every show, bfcache or not, so a normal reload is covered too. */
  window.addEventListener('pageshow', function () {
    placing = false;
    var btn = document.getElementById('drPlace');
    if (btn) { btn.disabled = false; btn.textContent = 'Place order'; }
  });

  /* ------------------------------------------------------------------
     Lifecycle
     ------------------------------------------------------------------ */
  function teardown() {
    clearInterval(loopTimer);
    clearTimeout(resetTimer);
    if (io) { io.disconnect(); }
    if (onScroll) { window.removeEventListener('scroll', onScroll); }
    loopTimer = resetTimer = io = onScroll = null;
    document.body.style.overflow = '';
  }

  function init() {
    teardown();

    el = {
      nav:       document.getElementById('nav'),
      scene:     document.getElementById('scene'),
      cnt:       document.getElementById('cnt'),
      picker:    document.getElementById('picker'),
      drLines:   document.getElementById('drLines'),
      badge:     document.getElementById('badge'),
      drNext:    document.getElementById('drNext'),
      drFoot:    document.getElementById('drFoot'),
      reviewBtn: document.getElementById('reviewBtn'),
      drawer:    document.getElementById('drawer'),
      scrim:     document.getElementById('scrim'),
      cartBtn:   document.getElementById('cartBtn')
    };

    /* Section isn't on this template — nothing to wire up. */
    if (!el.nav || !el.picker) { return; }

    /* sticky nav */
    onScroll = function () { el.nav.classList.toggle('stuck', window.scrollY > 24); };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    /* hero tap demo */
    if (el.scene) {
      el.scene.addEventListener('click', function () { playTap(); startLoop(); });
      el.scene.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          playTap();
          startLoop();
        }
      });
      setTimeout(function () { playTap(); startLoop(); }, reduce ? 0 : 1200);
    }

    /* scroll reveals */
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e, i) {
        if (e.isIntersecting) {
          setTimeout(function () { e.target.classList.add('in'); }, reduce ? 0 : i * 90);
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.16, rootMargin: '0px 0px -60px 0px' });
    Array.prototype.forEach.call(document.querySelectorAll('.reveal, .step'), function (n) {
      io.observe(n);
    });

    /* drawer controls */
    el.cartBtn.addEventListener('click', function () { openDrawer(1); });
    document.getElementById('drClose').addEventListener('click', closeDrawer);
    document.getElementById('drDone').addEventListener('click', closeDrawer);
    el.scrim.addEventListener('click', closeDrawer);
    el.drNext.addEventListener('click', function () { goStep(2); });
    document.getElementById('drBack').addEventListener('click', function () { goStep(1); });
    document.getElementById('drPlace').addEventListener('click', placeOrder);
    el.reviewBtn.addEventListener('click', function () { openDrawer(2); });

    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Shopify theme editor hooks */
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', teardown);
})();